import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { Router } from '@angular/router';
import { RequestService } from '@df/api/request/request.service';
import { App } from '@df/core/app';
import { MultisiteService } from '@df/core/service/multisite';
import { Status } from '@df/core/status';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentField } from '@icms/core/content';
import { Section2Component } from '../../common/section2/section2.component';
import { ActionComponent } from '../../ui/api/action.component';
import { AccountBackComponent } from '../back/account-back.component';
import { ResultComponent } from '../../ui/resut/result.component';

export namespace NStockLevelComponent {
    export const componentId = 'account-stock-level';

    export interface IComponentData {
        items: IItem[];
    }

    export interface IItem {
        title: string;
        description?: string;
        links?: ILink[];
        displayButtton?: boolean;
        hideForRestrictedAccounts?: boolean;
    }

    export interface ILink {
        linkText: string;
        url: string;
    }
}

@Component({
    selector: 'account-stock-level-report',
    templateUrl: './account-stock-level-report.html',
    styleUrls: ['./account-stock-level-report.scss'],
    standalone: true,
    imports: [
        AccountBackComponent,
        TranslationModule,
        Section2Component,
        ICmsClientModule,
        HrefComponent,
        ActionComponent,
        ResultComponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
@ICmsComponent<AccountStockLevelReportComponent>({
    id: NStockLevelComponent.componentId,
    block: {
        url: NStockLevelComponent.componentId
    },
    onDemandOnly: true,
    name: 'Account Stock Level',
    fields: [
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'items',
            title: 'Items',
            itemNameField: 'title',
            itemName: 'Item',
            required: true,
            fields: [
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'title',
                    title: 'Title',
                    required: true
                },
                {
                    type: NICmsContentField.EFieldType.textarea,
                    key: 'description',
                    title: 'Description'
                },
                <NICmsContentField.IRepeaterField>{
                    type: NICmsContentField.EFieldType.repeater,
                    key: 'links',
                    title: 'Links',
                    itemName: 'Link',
                    itemNameField: 'linkText',
                    fields: [
                        {
                            type: NICmsContentField.EFieldType.text,
                            key: 'linkText',
                            title: 'Link Text',
                            required: true
                        },
                        {
                            type: NICmsContentField.EFieldType.text,
                            key: 'url',
                            title: 'URL',
                            required: true
                        }
                    ]
                },
                {
                    type: NICmsContentField.EFieldType.toggle,
                    key: 'displayButtton',
                    title: 'Display Button',
                    default: false,
                    labels: {
                        true: 'Yes',
                        false: 'No'
                    }
                },
                {
                    type: NICmsContentField.EFieldType.toggle,
                    key: 'hideForRestrictedAccounts',
                    title: 'Hide for Restricted Accounts',
                    default: false,
                    labels: {
                        true: 'Yes',
                        false: 'No'
                    }
                },
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'restrictedAccountButtonText',
                    title: 'Restricted Account Button Text',
                    default: 'Please note that generating the report will take up to a minute.'
                }
            ],
            default: [
                {
                    title: 'Stock Level Report - csv',
                    displayButtton: true
                }
            ]
        }
    ]
})
export class AccountStockLevelReportComponent extends UiComponent {
    @ICmsContent({
        type: 'blockContent'
    })
    content?: NStockLevelComponent.IComponentData;

    private app = inject(App);
    private multisite = inject(MultisiteService);
    private requestService = inject(RequestService);
    private router = inject(Router);

    protected stockLevelReportStatus = new Status();

    get mediaFeedUrl() {
        return this.app.dfConfig.endpoint.mage + '/media/feed/' + this.app.dfConfig.ms.siteId + '_stock.csv';
    }

    protected downloadStockLevelReportCall(): void {
        this.requestService.send(
            this.stockLevelReportStatus,
            {
                path: 'https://n8n.tomandco.uk/webhook/623853ae-fa41-4f77-a6dd-a6508e3c32a2',
                data: {
                    store_code: this.multisite.current.storeCode,
                    store_id: this.multisite.current.id,
                    allowed_subcategory: this.customerAllowedSubcategory,
                    allowed_seasons: this.customerAllowedSeasons
                }
            },
            result => {
                this.downloadFileWithCustomName(result.url, 'stock-level-report.csv');
            }
        );
    }

    protected downloadFileWithCustomName(url: string, filename: string): void {
        const link = document.createElement('a');
        link.style.display = 'none';
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    protected shouldShowContentItem(item: NStockLevelComponent.IItem): boolean {
        return !(this.isAccountRestricted && item.hideForRestrictedAccounts);
    }

    private get customerAllowedSeasons(): number[] | undefined {
        return this.app.customer.model.data.allowed_seasons;
    }

    private get customerAllowedSubcategory(): number[] | undefined {
        return this.app.customer.model.data.allowed_subcategory;
    }

    protected get isAccountRestricted(): boolean {
        return !!(this.customerAllowedSeasons || this.customerAllowedSubcategory);
    }
}
